import { useEffect, useRef, useCallback } from 'react';
import { recordSessionEnd, SESSION_END_REASONS } from '../utils/sessionEnd.generated';

/**
 * useSessionManager
 *
 * Handles two concerns:
 *  1. **Token auto-refresh** – silently refreshes the JWT access token before it
 *     expires so users are never logged out while actively using the app.
 *  2. **Idle timeout**, which is OFF BY DEFAULT.
 *
 *     A session now ends when the person ends it. `keepSignedIn` in the global
 *     settings is true unless a system administrator turns it off, and while it
 *     is true nothing here signs anyone out — the access token is refreshed
 *     silently for as long as the refresh token lives.
 *
 *     The old behaviour was an idle timer that signed people out for not
 *     touching the screen, which in a classroom mostly meant losing a
 *     half-entered register: a teacher marking a book for ten minutes came back
 *     to a login form. A deployment that genuinely needs idle expiry turns the
 *     setting off, and the per-role minimums below apply again.
 *
 * @param {object|null|undefined} user  – current user object from UserContext
 */

const configuredBaseRaw = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.VITE_API_BASE_URL ||
  process.env.NEXT_PUBLIC_API_BASE_URL ||
  ''
).trim();

const browserHost = typeof window !== 'undefined' ? window.location.hostname : '';
const isLocalBrowserHost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(browserHost);
const isLocalNodeEnv = process.env.NODE_ENV === 'development';

const API_BASE = configuredBaseRaw || ((isLocalBrowserHost || isLocalNodeEnv) ? 'http://localhost:5000' : '');

const isProd = process.env.NODE_ENV === 'production';

// ---------- helpers ----------

function parseJwtExp(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

function getIdleTimeoutMs(role, sessionTimeoutMinutes, keepSignedIn) {
  // ZERO MEANS NEVER. `resetIdleTimer` and the setup effect both treat a falsy
  // value as "no idle timeout for this session", which is the branch that was
  // already there for parents and students — this simply makes it the default
  // for everyone.
  if (keepSignedIn) return 0;

  const min = Number(sessionTimeoutMinutes);
  // Default to 8 hours if not configured
  const resolvedMinutes = Number.isFinite(min) && min > 0 ? min : 480;
  // Enforce system minimum of 8 hours
  const finalMinutes = Math.max(480, resolvedMinutes);
  return finalMinutes * 60 * 1000;
}

// ---------- hook ----------

export default function useSessionManager(user) {
  const refreshTimerRef = useRef(null);
  const idleTimerRef = useRef(null);
  const idleTimeoutMsRef = useRef(null);
  const sessionTimeoutMinutesRef = useRef(null);
  // Defaults to ON before the settings arrive, so the very first minutes of a
  // session cannot expire while the answer is still in flight.
  const keepSignedInRef = useRef(true);

  // ---- token auto-refresh ----
  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    const exp = parseJwtExp(token);
    if (!exp) return;

    const nowSec = Math.floor(Date.now() / 1000);
    const secondsLeft = exp - nowSec;

    // Refresh 5 minutes before expiry (or immediately if <5 min left)
    const BUFFER_SEC = 5 * 60;
    const delaySec = Math.max(0, secondsLeft - BUFFER_SEC);
    const delayMs = delaySec * 1000;

    refreshTimerRef.current = setTimeout(async () => {
      try {
        const storedRefresh =
          localStorage.getItem('refreshToken') ||
          localStorage.getItem('REFRESH_TOKEN') ||
          sessionStorage.getItem('refreshToken') ||
          '';

        const base = API_BASE.replace(/\/$/, '');
        let res = await fetch(`${base}/api/auth/refresh-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(storedRefresh ? { refreshToken: storedRefresh } : {}),
        });

        if (!res.ok) {
          res = await fetch(`${base}/auth/refresh-token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(storedRefresh ? { refreshToken: storedRefresh } : {}),
          });
        }

        if (res.ok) {
          const data = await res.json();
          if (data?.accessToken) {
            localStorage.setItem('accessToken', data.accessToken);
            try { localStorage.setItem('token', data.accessToken); } catch (_) {}
            // THE ROTATED REFRESH TOKEN. The server issues a new one on every
            // refresh so the expiry window slides forward from LAST USE rather
            // than counting down from sign-in. Dropping it here would leave the
            // original in place and the session would still end on a fixed date,
            // no matter how much the app had been used in between — which is the
            // exact behaviour this is meant to remove.
            if (data.refreshToken) {
              try { localStorage.setItem('refreshToken', data.refreshToken); } catch (_) {}
            }
            // Notify other tabs/components
            try { window.dispatchEvent(new Event('app:auth:changed')); } catch (_) {}
            // Schedule the next refresh based on the new token
            scheduleRefresh();
          }
        } else {
          // Refresh failed → force re-login
          if (!isProd) console.warn('[SessionManager] Token refresh failed, logging out');
          recordSessionEnd(SESSION_END_REASONS.REFRESH_FAILED);
          localStorage.removeItem('accessToken');
          localStorage.removeItem('token');
          try { window.dispatchEvent(new CustomEvent('app:auth:required')); } catch (_) {}
        }
      } catch (err) {
        if (!isProd) console.error('[SessionManager] Token refresh error:', err);
      }
    }, delayMs);
  }, []);

  // ---- idle timeout ----
  const doLogout = useCallback(() => {
    // Only reachable where a deployment has turned keepSignedIn off, and that is
    // exactly why it must say so — otherwise it looks like the bug this setting
    // was introduced to remove.
    recordSessionEnd(SESSION_END_REASONS.IDLE);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('token');
    try { window.dispatchEvent(new CustomEvent('app:auth:required')); } catch (_) {}
  }, []);

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    const ms = idleTimeoutMsRef.current;
    if (!ms) return; // no idle timeout for this role
    idleTimerRef.current = setTimeout(doLogout, ms);
  }, [doLogout]);

  // ---- setup / teardown ----
  useEffect(() => {
    if (!user || user === undefined) {
      // Not logged in – clear everything
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      return;
    }

    // Load/cached session timeout (minutes) from global settings.
    // This controls the idle logout behavior.
    (async () => {
      try {
        const cached = localStorage.getItem('globalSettings_sessionTimeout');
        if (cached && sessionTimeoutMinutesRef.current == null) {
          const parsed = Number(cached);
          if (Number.isFinite(parsed) && parsed > 0) {
            sessionTimeoutMinutesRef.current = parsed;
          }
        }
        const cachedKeep = localStorage.getItem('globalSettings_keepSignedIn');
        if (cachedKeep != null) keepSignedInRef.current = cachedKeep !== 'false';

        const base = API_BASE.replace(/\/$/, '');
        const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || '';
        const res = await fetch(`${base}/api/global-settings`, {
          method: 'GET',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
        });

        if (!res.ok) return;
        const json = await res.json();
        const keep = json?.settings?.keepSignedIn !== false;
        keepSignedInRef.current = keep;
        try { localStorage.setItem('globalSettings_keepSignedIn', String(keep)); } catch (_) {}

        const minutes = Number(json?.settings?.sessionTimeout);
        if (Number.isFinite(minutes) && minutes > 0) {
          sessionTimeoutMinutesRef.current = minutes;
          try { localStorage.setItem('globalSettings_sessionTimeout', String(minutes)); } catch (_) {}
        }

        const role = (user?.role || '').toString();
        const timeoutMs = getIdleTimeoutMs(role, sessionTimeoutMinutesRef.current, keep);
        idleTimeoutMsRef.current = timeoutMs;
        // Cancels a timer armed before the answer arrived, so switching the
        // setting on takes effect without a reload.
        if (timeoutMs) resetIdleTimer();
        else if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      } catch (err) {
        if (!isProd) console.warn('[SessionManager] Failed to load global session timeout:', err?.message || err);
      }
    })();

    // --- auto-refresh ---
    scheduleRefresh();

    // --- idle timeout ---
    const role = (user?.role || '').toString();
    const timeoutMs = getIdleTimeoutMs(role, sessionTimeoutMinutesRef.current, keepSignedInRef.current);
    idleTimeoutMsRef.current = timeoutMs;

    if (timeoutMs) {
      resetIdleTimer();
      const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];
      events.forEach((e) => window.addEventListener(e, resetIdleTimer, { passive: true }));

      return () => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        events.forEach((e) => window.removeEventListener(e, resetIdleTimer));
      };
    }

    // No idle timeout for this role – just clean up refresh timer on unmount
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [user, scheduleRefresh, resetIdleTimer]);

  // Re-schedule refresh whenever the token changes in storage (e.g. from another tab)
  useEffect(() => {
    const authHandler = () => scheduleRefresh();
    const storageHandler = (e) => {
      if (e?.key === 'accessToken' || e?.key === 'token') scheduleRefresh();
    };
    window.addEventListener('app:auth:changed', authHandler);
    window.addEventListener('storage', storageHandler);
    return () => {
      window.removeEventListener('app:auth:changed', authHandler);
      window.removeEventListener('storage', storageHandler);
    };
  }, [scheduleRefresh]);
}
